import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Send, ClipboardCheck, Mail, MessageSquare, ImagePlus, ChevronLeft, FilePlus2 } from 'lucide-react';
import { Customer } from '../lib/customers';
import { StockItem } from '../lib/stockItems';
import { uploadPurchaseOrderItemImage } from '../lib/purchaseOrders';

// Type alias for better readability - suppliers are customers with is_supplier: true
type Supplier = Customer;
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { sendSMS } from '../lib/messaging';
import { sendGmailEmail } from '../lib/email';
import PurchaseOrderSendModal from './PurchaseOrderSendModal';

// Grow the comments box with its content, capped at ~6 lines (then scroll).
const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
};

interface MeasurementData {
  name?: string;
  category?: string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
  area?: number | string;
  unit?: string;
  custom_fields?: Record<string, any>;
  notes?: string;
  photos?: Array<{ url: string }>;
}

interface OrderSummaryPDFModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, the header Back button uses this instead of onClose — e.g. to
   * return to the Create Custom Order screen the user came from. */
  onBack?: () => void;
  supplier: Supplier;
  items: Array<{
    item: StockItem;
    quantityToOrder: number;
    totalCost: number;
    measurement?: MeasurementData | null;
    comment?: string | null;
    images?: string[] | null;
  }>;
  totalAmount: number;
  // May return the final PO number (string) when the DB assigns a sequential
  // number at send time — the email/SMS then use that number instead of the
  // local placeholder. Returning true keeps the placeholder (legacy behavior).
  onSendOrder: (orderNumber: string) => Promise<boolean | string> | boolean | string;
  onItemsChange?: (updatedItems: Array<{
    item: StockItem;
    quantityToOrder: number;
    totalCost: number;
    measurement?: MeasurementData | null;
    comment?: string | null;
    images?: string[] | null;
  }>, newTotal: number) => void;
  onEmailSent?: (orderNumber: string, messageId: string, threadId: string, emailBody: string) => void;
  /**
   * Called after ANY successful send (email or SMS) so the parent can close
   * the whole order flow — not just this summary modal.
   */
  onOrderSent?: () => void;
}

export default function OrderSummaryPDFModal({
  isOpen,
  onClose,
  onBack,
  supplier,
  items,
  totalAmount,
  onSendOrder,
  onItemsChange,
  onEmailSent,
  onOrderSent
}: OrderSummaryPDFModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'main' | 'pdf'>('main');
  const [showSendDropdown, setShowSendDropdown] = useState(false);
  const [composeMode, setComposeMode] = useState<'email' | 'sms' | null>(null);
  // "Create Only": the PO was saved without notifying the supplier.
  const [createOnlyDone, setCreateOnlyDone] = useState(false);
  const [isCreatingOnly, setIsCreatingOnly] = useState(false);
  // The PO row is created once; failed notification sends can be retried
  // through the compose modal without creating a duplicate order.
  const [sentPoNumber, setSentPoNumber] = useState<string | null>(null);
  const [editableItems, setEditableItems] = useState(items);
  const [editableTotal, setEditableTotal] = useState(totalAmount);
  const [orderNumber, setOrderNumber] = useState(`PO-${Date.now().toString().slice(-6)}`);
  const [orderDate, setOrderDate] = useState(new Date().toLocaleDateString());
  const [uploadingImagesFor, setUploadingImagesFor] = useState<string | null>(null);
  const imageInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // Server-generated PDF preview (blob URL) — the same document that gets
  // downloaded and attached to the supplier email.
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState<string | null>(null);

  // Generate PO number and date only once when modal opens (not on every render)
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setOrderSent(false);
      setSmsSent(false);
      setSmsError(null);
      setEmailSent(false);
      setEmailError(null);
      setActiveTab('main');
      setShowSendDropdown(false);
      setComposeMode(null);
      setSentPoNumber(null);
      setCreateOnlyDone(false);
      setIsCreatingOnly(false);
      // Generate PO number and date only once when modal first opens
      setOrderNumber(`PO-${Date.now().toString().slice(-6)}`);
      setOrderDate(new Date().toLocaleDateString());
      // Reset editable items to current items when modal opens
      setEditableItems(items);
      setEditableTotal(totalAmount);
    }
  }, [isOpen]); // Only depend on isOpen, not items/totalAmount to prevent PO number regeneration

  // Close the send dropdown on outside click/tap (mirrors BetaPaymentsModal).
  useEffect(() => {
    if (!showSendDropdown) return;
    const close = () => setShowSendDropdown(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [showSendDropdown]);

  // Update editable items when items prop changes
  useEffect(() => {
    setEditableItems(items);
    setEditableTotal(totalAmount);
  }, [items, totalAmount]);

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 0) return;
    
    const updatedItems = editableItems.map(orderItem => {
      if (orderItem.item.id === itemId) {
        const newTotalCost = newQuantity * orderItem.item.acquisition_cost;
        return {
          ...orderItem,
          quantityToOrder: newQuantity,
          totalCost: newTotalCost
        };
      }
      return orderItem;
    });
    
    const newTotal = updatedItems.reduce((sum, item) => sum + item.totalCost, 0);
    
    setEditableItems(updatedItems);
    setEditableTotal(newTotal);
    
    // Notify parent component of changes
    if (onItemsChange) {
      onItemsChange(updatedItems, newTotal);
    }
  };

  const handleCommentChange = (itemId: string, comment: string) => {
    const updatedItems = editableItems.map(orderItem =>
      orderItem.item.id === itemId ? { ...orderItem, comment } : orderItem
    );
    setEditableItems(updatedItems);
    if (onItemsChange) {
      onItemsChange(updatedItems, editableTotal);
    }
  };

  const handleImagesChange = (itemId: string, images: string[]) => {
    const updatedItems = editableItems.map(orderItem =>
      orderItem.item.id === itemId ? { ...orderItem, images: images.length ? images : null } : orderItem
    );
    setEditableItems(updatedItems);
    if (onItemsChange) {
      onItemsChange(updatedItems, editableTotal);
    }
  };

  const handleAddImages = async (itemId: string, files: FileList | null) => {
    if (!files || files.length === 0 || !selectedBusiness?.id) return;
    try {
      setUploadingImagesFor(itemId);
      const urls: string[] = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        urls.push(await uploadPurchaseOrderItemImage(selectedBusiness.id, file));
      }
      if (urls.length > 0) {
        const existing = editableItems.find(oi => oi.item.id === itemId)?.images || [];
        handleImagesChange(itemId, [...existing, ...urls]);
      }
    } catch (err) {
      console.error('Error uploading item image:', err);
    } finally {
      setUploadingImagesFor(null);
      const input = imageInputRefs.current[itemId];
      if (input) input.value = '';
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  /**
   * Generate the purchase order PDF server-side (generate-purchase-order-pdf,
   * pdf-lib — same architecture as invoices). The preview tab, the Download
   * button and the email attachment all use this exact document.
   */
  const generatePoPdf = async (
    poNumber: string
  ): Promise<{ blob: Blob; base64: string; filename: string } | null> => {
    if (!selectedBusiness?.id) return null;
    try {
      const { data, error } = await supabase.functions.invoke('generate-purchase-order-pdf', {
        body: {
          order: {
            businessId: selectedBusiness.id,
            orderNumber: poNumber,
            orderDateLabel: orderDate,
            supplier: {
              name: `${supplier.first_name || ''} ${supplier.last_name || ''}`.trim() || 'Supplier',
              email: supplier.email || null,
              phone: supplier.phone || null,
            },
            items: editableItems.map(({ item, quantityToOrder, comment, images }) => ({
              name: item.name,
              unit: item.unit,
              quantity: quantityToOrder,
              unitCost: item.acquisition_cost > 0 ? item.acquisition_cost : null,
              comment: comment || null,
              images: images || [],
            })),
            total: editableTotal,
          },
        },
      });

      if (error || !data?.base64) {
        console.error('Error generating PO PDF:', error, data?.error);
        return null;
      }

      const binaryStr = atob(data.base64);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      return { blob, base64: data.base64, filename: data.filename || `${poNumber}.pdf` };
    } catch (error) {
      console.error('Error generating PO PDF:', error);
      return null;
    }
  };

  // Build the PDF preview whenever the PDF tab is opened (items are edited on
  // the Order tab, so regenerating on tab switch keeps the preview current).
  useEffect(() => {
    if (!isOpen || activeTab !== 'pdf') return;
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      setPdfPreviewLoading(true);
      setPdfPreviewError(null);
      const pdfData = await generatePoPdf(sentPoNumber || orderNumber);
      if (cancelled) return;
      if (pdfData) {
        objectUrl = URL.createObjectURL(pdfData.blob);
        setPdfPreviewUrl(objectUrl);
      } else {
        setPdfPreviewError('Could not generate the PDF preview. You can still send the order.');
      }
      setPdfPreviewLoading(false);
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPdfPreviewUrl(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab]);

  const handleDownloadPdf = async () => {
    try {
      setIsGeneratingPdf(true);

      const pdfData = await generatePoPdf(sentPoNumber || orderNumber);
      if (!pdfData) return;

      // Same download/share behavior as invoices (native share on device).
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const file = new File([pdfData.blob], pdfData.filename, { type: 'application/pdf' });
        if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: pdfData.filename }).catch(() => {});
          return;
        }
      }
      const url = URL.createObjectURL(pdfData.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Delayed revoke: revoking synchronously can silently cancel the download.
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const createSmsMessage = (poNumber: string = orderNumber) => {
    // Helper function to clean text for SMS (same as email)
    const cleanText = (text: string): string => {
      if (!text) return '';
      return text
        .replace(/[^\x00-\xFF]/g, '') // Remove non-Latin1 characters (keep only 0-255 range)
        .replace(/[""]/g, '"') // Replace smart quotes with regular quotes
        .replace(/['']/g, "'") // Replace smart apostrophes with regular apostrophes
        .replace(/[–—]/g, '-') // Replace em/en dashes with regular hyphens
        .replace(/…/g, '...') // Replace ellipsis with three dots
        .replace(/[®©™]/g, '') // Remove trademark symbols
        .replace(/[°]/g, ' degrees') // Replace degree symbol
        .replace(/[½¼¾]/g, '') // Remove fraction symbols
        .replace(/[§¶]/g, '') // Remove section and paragraph symbols
        .trim();
    };

    let message = `New Order\n`;
    message += `${poNumber}\n`;
    message += `Date: ${orderDate}\n\n`;
    
    editableItems.forEach(({ item, quantityToOrder, comment, images }) => {
      const itemName = cleanText(item.name || 'Item');
      message += `-${itemName} - ${quantityToOrder}\n`;
      const cleanComment = cleanText(comment || '');
      if (cleanComment) {
        message += ` ${cleanComment}\n`;
      }
      // Reference photos go along as plain links in SMS.
      (images || []).forEach(url => {
        message += ` ${url}\n`;
      });
    });
    
    return message;
  };

  /**
   * Compact email body — mirrors the invoice email: a short personal message
   * with the purchase order attached as a PDF. The PDF (generated server-side)
   * is the document; the email no longer duplicates it with a separate layout.
   */
  const createEmailMessage = (poNumber: string = orderNumber, introMessage?: string) => {
    const escapeHtml = (t: string) =>
      t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const message = introMessage?.trim() || defaultEmailMessage;

    return `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
    <div style="font-size: 14px; line-height: 1.6; white-space: pre-wrap;">${escapeHtml(message)}</div>
    <div style="margin-top: 20px; padding: 14px 16px; background: #f8f9fa; border-left: 3px solid #007bff; border-radius: 6px; font-size: 13px; color: #555;">
        Purchase order <strong>${escapeHtml(poNumber)}</strong> is attached as a PDF with full item details.
    </div>
</div>`;
  };

  /**
   * Create the PO in the database exactly once (the DB assigns the real
   * sequential number). Retries after a failed notification reuse the same PO.
   */
  const ensurePurchaseOrderCreated = async (): Promise<string | null> => {
    if (sentPoNumber) return sentPoNumber;
    const result = await onSendOrder(orderNumber);
    if (!result) return null;
    const finalNumber = typeof result === 'string' && result ? result : orderNumber;
    setSentPoNumber(finalNumber);
    setOrderSent(true);
    return finalNumber;
  };

  // "Create Only": save the PO in the database without notifying the supplier.
  // The parent shows its own success/error toasts for the creation itself.
  const handleCreateOnly = async () => {
    try {
      setIsSubmitting(true);
      setIsCreatingOnly(true);
      const finalNumber = await ensurePurchaseOrderCreated();
      if (!finalNumber) return;
      if (finalNumber !== orderNumber) setOrderNumber(finalNumber);
      setCreateOnlyDone(true);
      // Close the entire flow (summary + create-order modal behind it).
      setTimeout(() => {
        onClose();
        onOrderSent?.();
      }, 1500);
    } catch (error) {
      console.error('Error creating order:', error);
    } finally {
      setIsSubmitting(false);
      setIsCreatingOnly(false);
    }
  };

  // Invoice-style send: the compose modal hands back the user-edited
  // recipient/subject/message; we create the PO, swap the placeholder number
  // for the DB-assigned one, and send through the chosen channel.
  const handleComposedSend = async ({ recipient, subject, message }: { recipient: string; subject: string; message: string }) => {
    if (!selectedBusiness?.id || !composeMode) return;
    const mode = composeMode;

    try {
      setIsSubmitting(true);
      const placeholderNumber = orderNumber;
      const finalNumber = await ensurePurchaseOrderCreated();
      if (!finalNumber) return;
      if (finalNumber !== orderNumber) setOrderNumber(finalNumber);
      // The compose defaults reference the local placeholder number; swap in
      // the real one everywhere it appears.
      const fix = (t: string) => t.split(placeholderNumber).join(finalNumber);

      if (mode === 'email') {
        // Generate the PO PDF server-side and attach it — exactly like invoices.
        const pdfData = await generatePoPdf(finalNumber);
        if (!pdfData) {
          setEmailError('Failed to generate the purchase order PDF. Use Send to try again.');
          return;
        }
        const emailBody = createEmailMessage(finalNumber, fix(message));
        // The [ORDER-...] tag keeps supplier replies threading back to this PO.
        const finalSubject = `[ORDER-${finalNumber}] ${fix(subject)}`;
        const result = await sendGmailEmail({
          businessId: selectedBusiness.id,
          to: recipient,
          subject: finalSubject,
          body: emailBody,
          attachments: [{
            filename: pdfData.filename,
            content: pdfData.base64,
            type: 'application/pdf',
          }],
        });
        if (result.success) {
          setEmailSent(true);
          setEmailError(null);
          setComposeMode(null);
          if (onEmailSent && result.messageId) {
            onEmailSent(finalNumber, result.messageId, result.threadId || result.messageId, emailBody);
          }
          // Close the entire flow (summary + create-order modal behind it).
          setTimeout(() => {
            onClose();
            onOrderSent?.();
          }, 1500);
        } else {
          setEmailError(result.error || 'Failed to send email notification');
        }
      } else {
        const result = await sendSMS({
          businessId: selectedBusiness.id,
          to: recipient,
          message: fix(message),
        });
        if (result.success) {
          setSmsSent(true);
          setSmsError(null);
          setComposeMode(null);
          // Close the entire flow (summary + create-order modal behind it).
          setTimeout(() => {
            onClose();
            onOrderSent?.();
          }, 1500);
        } else {
          setSmsError(result.error || 'Failed to send SMS notification');
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error sending order';
      if (mode === 'email') setEmailError(errorMessage);
      else setSmsError(errorMessage);
      console.error('Error sending order:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasUnpricedItems = editableItems.some(({ item }) => item.acquisition_cost <= 0);
  const supplierFirstName = (supplier.first_name || '').trim() || 'there';
  const defaultEmailSubject = `Purchase Order ${orderNumber}`;
  const defaultEmailMessage =
    `Hi ${supplierFirstName},\n\n` +
    `Please find our purchase order ${orderNumber} attached.` +
    (hasUnpricedItems ? '\n\nSome items are pending pricing — please reply with a quote.' : '') +
    `\n\nThank you,\n${selectedBusiness?.name || ''}`;

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100100,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[700px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100150
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            {/* Back — returns to the previous step in the flow (custom order
                screen when the summary was opened from it, otherwise closes
                back to the order building step) */}
            <button
              onClick={onBack || onClose}
              className="absolute top-1/2 -translate-y-1/2 left-4 flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <ChevronLeft className="h-5 w-5" />
              Back
            </button>
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Order Summary
            </h2>
            <div className="text-sm text-gray-500 mt-1">
              Purchase order #{orderNumber}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-white px-4 sm:px-6">
            <button
              type="button"
              onClick={() => setActiveTab('main')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors duration-150 touch-manipulation ${
                activeTab === 'main'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Order
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('pdf')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors duration-150 touch-manipulation ${
                activeTab === 'pdf'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              PDF
            </button>
          </div>

          {/* Content Section */}
          <div 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {activeTab === 'main' && (<>
              {/* Editable Order Items */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Order Items</h4>
                <div className="space-y-3">
                  {editableItems.map(({ item, quantityToOrder, totalCost, comment, images }) => (
                    <div key={item.id} className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                      <div className="flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <h5 className="text-sm font-semibold text-gray-900">{item.name}</h5>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-600">
                            <span>Unit: {item.unit}</span>
                            <span>
                              Price: {item.acquisition_cost > 0
                                ? `$${item.acquisition_cost.toFixed(2)}/${item.unit}`
                                : 'TBD — awaiting quote'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="text-xs text-gray-600 whitespace-nowrap">Qty:</label>
                          <input
                            type="number"
                            min="0"
                            value={quantityToOrder}
                            onChange={(e) => {
                              const newQty = parseInt(e.target.value) || 0;
                              handleQuantityChange(item.id, newQty);
                            }}
                            className="w-20 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <span className="text-xs text-gray-500">{item.unit}</span>
                          <span className="text-sm font-semibold text-gray-900 ml-2 min-w-[60px] text-right">
                            {item.acquisition_cost > 0 ? `$${totalCost.toFixed(2)}` : 'TBD'}
                          </span>
                        </div>
                      </div>
                      <textarea
                        ref={autoResizeTextarea}
                        value={comment || ''}
                        onChange={(e) => {
                          handleCommentChange(item.id, e.target.value);
                          autoResizeTextarea(e.currentTarget);
                        }}
                        placeholder="Item comments for the supplier (specs, measurements, etc.)"
                        rows={1}
                        className="w-full mt-2 px-3 py-2 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        style={{ maxHeight: 140, overflowY: 'auto' }}
                      />
                      {/* Reference images */}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {(images || []).map((url) => (
                          <div key={url} className="relative group">
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              <img
                                src={url}
                                alt="Reference"
                                className="h-12 w-12 object-cover rounded-lg border border-gray-200"
                              />
                            </a>
                            <button
                              type="button"
                              onClick={() => handleImagesChange(item.id, (images || []).filter(u => u !== url))}
                              className="absolute -top-1.5 -right-1.5 h-5 w-5 flex items-center justify-center bg-gray-800 text-white rounded-full shadow hover:bg-red-600 transition-colors"
                              aria-label="Remove image"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        <input
                          ref={(el) => { imageInputRefs.current[item.id] = el; }}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => handleAddImages(item.id, e.target.files)}
                        />
                        <button
                          type="button"
                          onClick={() => imageInputRefs.current[item.id]?.click()}
                          disabled={uploadingImagesFor === item.id}
                          className="h-12 w-12 flex flex-col items-center justify-center gap-0.5 text-gray-400 hover:text-blue-600 bg-white border border-dashed border-gray-300 hover:border-blue-400 rounded-lg transition-colors disabled:opacity-50"
                          aria-label="Add reference images"
                        >
                          {uploadingImagesFor === item.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                          ) : (
                            <ImagePlus className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-900">Total Amount:</span>
                  <span className="text-lg font-semibold text-blue-600">${editableTotal.toFixed(2)}</span>
                </div>
                {editableItems.some(({ item }) => item.acquisition_cost <= 0) && (
                  <p className="mt-2 text-xs text-amber-600">
                    Some items are awaiting a quote (TBD) and aren't included in the total. You can add pricing
                    to this PO after the supplier responds.
                  </p>
                )}
              </div>

              {/* Success Message */}
              {orderSent && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-sm text-green-700 flex items-center font-semibold">
                    <ClipboardCheck className="h-4 w-4 mr-2" />
                    Order {sentPoNumber || orderNumber} created for {`${supplier.first_name} ${supplier.last_name}`.trim()}!
                  </p>
                  <div className="mt-2 text-xs text-green-600 space-y-1">
                    {smsSent && <div className="flex items-center"><span className="text-green-500 mr-1">✓</span> SMS sent</div>}
                    {emailSent && <div className="flex items-center"><span className="text-green-500 mr-1">✓</span> Email sent</div>}
                    {createOnlyDone && !smsSent && !emailSent && (
                      <div>Saved without sending — you can send it to the supplier later.</div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Error Messages */}
              {(smsError || emailError) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-sm text-yellow-700 flex items-center font-semibold">
                    <span className="mr-2">⚠️</span>
                    Order created but some notifications failed:
                  </p>
                  <div className="mt-2 text-xs text-yellow-600 space-y-1">
                    {smsError && <div>SMS failed: {smsError}</div>}
                    {emailError && <div>Email failed: {emailError}</div>}
                  </div>
                  <p className="mt-2 text-xs text-yellow-700">
                    Use the Send button below to try again — the same order will be re-sent, not duplicated.
                  </p>
                </div>
              )}
              
              {!supplier.phone && !supplier.email && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-sm text-gray-600">
                    This supplier has no saved contact info. You can still send the order — enter an email or phone number when composing the message.
                  </p>
                </div>
              )}
              </>)}

              {activeTab === 'pdf' && (<>
              {/* Live preview of the actual server-generated PDF — the same
                  document that gets downloaded and attached to the email. */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                {pdfPreviewLoading ? (
                  <div className="flex flex-col items-center justify-center py-24 text-sm text-gray-500 gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    Generating PDF preview...
                  </div>
                ) : pdfPreviewUrl ? (
                  <iframe
                    title="Purchase order PDF preview"
                    src={pdfPreviewUrl}
                    className="w-full"
                    style={{ height: '65vh', border: 'none' }}
                  />
                ) : (
                  <div className="py-16 px-6 text-center text-sm text-gray-500">
                    {pdfPreviewError || 'PDF preview unavailable.'}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={isGeneratingPdf || isSubmitting}
                className="w-full inline-flex justify-center items-center px-4 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Download className="mr-2 h-4 w-4" />
                {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
              </button>
              </>)}
            </div>
          </div>

          {/* Sticky footer: always-visible Send (invoice-style dropdown) */}
          <div
            className="border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4 flex-shrink-0"
            style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}
          >
            <div className="relative">
              {showSendDropdown && (
                <div
                  className="absolute bottom-full mb-2 right-0 w-full sm:w-56 rounded-xl shadow-lg bg-white border border-gray-200 z-20 overflow-hidden"
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowSendDropdown(false);
                      setComposeMode('email');
                    }}
                    className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation"
                    style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Mail className="h-4 w-4 mr-3 text-blue-500" />
                    <span>
                      Email
                      {supplier.email && <span className="block text-xs text-gray-400 font-normal">{supplier.email}</span>}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSendDropdown(false);
                      setComposeMode('sms');
                    }}
                    className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation border-t border-gray-100"
                    style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <MessageSquare className="h-4 w-4 mr-3 text-green-500" />
                    <span>
                      SMS
                      {supplier.phone && <span className="block text-xs text-gray-400 font-normal">{supplier.phone}</span>}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSendDropdown(false);
                      void handleCreateOnly();
                    }}
                    className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 touch-manipulation border-t border-gray-100"
                    style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <FilePlus2 className="h-4 w-4 mr-3 text-gray-500" />
                    <span>
                      Create Only
                      <span className="block text-xs text-gray-400 font-normal">Save the PO without sending it</span>
                    </span>
                  </button>
                </div>
              )}
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSendDropdown((v) => !v);
                }}
                disabled={isSubmitting || isGeneratingPdf || (orderSent && (smsSent || emailSent)) || createOnlyDone}
                className={`w-full inline-flex justify-center items-center px-4 py-3 text-sm font-semibold text-white rounded-xl active:scale-[0.98] transition-all duration-150 touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${
                  (orderSent && (smsSent || emailSent)) || createOnlyDone
                    ? 'bg-green-600'
                    : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {isCreatingOnly ? 'Creating...' : 'Sending...'}
                  </>
                ) : orderSent && (smsSent || emailSent) ? (
                  <>
                    <ClipboardCheck className="mr-2 h-4 w-4" />
                    Order Sent
                  </>
                ) : createOnlyDone ? (
                  <>
                    <ClipboardCheck className="mr-2 h-4 w-4" />
                    Order Created
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Create & Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice-style compose modal (email or SMS) */}
      <PurchaseOrderSendModal
        isOpen={composeMode !== null}
        mode={composeMode || 'email'}
        onClose={() => setComposeMode(null)}
        onSend={handleComposedSend}
        recipient={(composeMode === 'sms' ? supplier.phone : supplier.email) || ''}
        supplierName={`${supplier.first_name} ${supplier.last_name}`.trim() || 'Supplier'}
        isSending={isSubmitting}
        initialSubject={defaultEmailSubject}
        initialMessage={composeMode === 'sms' ? createSmsMessage() : defaultEmailMessage}
      />
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
} 